// School admin: the school's progress and released grades, one row per class, with CSV
// and XLSX export. Each class links to its own report on the class page.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { createLogger } from '../../utils/logger';
import { useDebouncedValue } from '../../hooks/use-debounced-value';
import LoadingIndicator from '../loading-indicator/LoadingIndicator';
import {
  ClassStatus,
  ExportFormat,
  SchoolReport,
  downloadSchoolReport,
  fetchSchoolReport,
} from '../../services/classService';
import ReportSummary, { formatAverage } from './ReportSummary';
import {
  cardStyle,
  errorMessage,
  errorStyle,
  hintStyle,
  inputStyle,
  secondaryButtonStyle,
  statusBadgeStyle,
  tableStyle,
  tdStyle,
  thStyle,
} from './classesStyles';

const logger = createLogger('SchoolReportTab');
const YEAR_DEBOUNCE_MS = 400;

interface Props {
  organizationId?: string | null;
}

const SchoolReportTab: React.FC<Props> = ({ organizationId }) => {
  const { t } = useTranslation();
  const [report, setReport] = useState<SchoolReport | null>(null);
  const [status, setStatus] = useState<ClassStatus | ''>('active');
  const [schoolYear, setSchoolYear] = useState('');
  const debouncedYear = useDebouncedValue(schoolYear.trim(), YEAR_DEBOUNCE_MS);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<ExportFormat | null>(null);

  useEffect(() => {
    // A slower, older request (a partly typed year) must not overwrite the current report.
    let cancelled = false;
    setError(null);
    fetchSchoolReport({ status, schoolYear: debouncedYear }, organizationId)
      .then(loaded => {
        if (!cancelled) setReport(loaded);
      })
      .catch(err => {
        if (cancelled) return;
        logger.error('Failed to load the school report', err);
        setError(errorMessage(err, t('studentAdmin.report.couldNotLoad')));
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, status, debouncedYear, t]);

  const exportAs = async (format: ExportFormat) => {
    setExporting(format);
    setError(null);
    try {
      await downloadSchoolReport({ status, schoolYear: debouncedYear }, format, 'school-report', organizationId);
    } catch (err) {
      logger.error('Failed to export the school report', err);
      setError(errorMessage(err, t('studentAdmin.report.couldNotExport')));
    } finally {
      setExporting(null);
    }
  };

  return (
    <section style={cardStyle} aria-labelledby="school-report-heading">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <h2 id="school-report-heading" style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>
            {t('studentAdmin.report.schoolTitle')}
          </h2>
          <p style={{ ...hintStyle, marginTop: 4 }}>{t('studentAdmin.report.averageHint')}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select
            aria-label={t('common.status')}
            style={inputStyle}
            value={status}
            onChange={e => setStatus(e.target.value as ClassStatus | '')}
          >
            <option value="active">{t('classes.statuses.active')}</option>
            <option value="archived">{t('classes.statuses.archived')}</option>
            <option value="">{t('common.all')}</option>
          </select>
          <input
            aria-label={t('classes.fields.school_year')}
            placeholder={t('classes.schoolYearPlaceholder')}
            style={{ ...inputStyle, width: 140 }}
            value={schoolYear}
            onChange={e => setSchoolYear(e.target.value)}
          />
          {(['csv', 'xlsx'] as ExportFormat[]).map(format => (
            <button
              key={format}
              type="button"
              style={secondaryButtonStyle}
              disabled={exporting !== null}
              onClick={() => exportAs(format)}
            >
              <Download size={14} /> {t(`studentAdmin.report.export.${format}`)}
            </button>
          ))}
        </div>
      </div>

      {error && <div style={{ ...errorStyle, marginTop: '1rem' }} role="alert">{error}</div>}

      {!report ? (
        !error && <LoadingIndicator message={t('studentAdmin.report.loading')} />
      ) : (
        <>
          <ReportSummary
            counts={report}
            extra={[
              { label: t('studentAdmin.report.classes'), value: report.classes },
              { label: t('classes.students'), value: report.students },
            ]}
          />
          {report.by_class.length === 0 ? (
            <p style={hintStyle}>{t('studentAdmin.report.noClasses')}</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>{t('common.name')}</th>
                    <th style={thStyle}>{t('classes.fields.school_year')}</th>
                    <th style={thStyle}>{t('classes.students')}</th>
                    <th style={thStyle}>{t('studentAdmin.report.assignments')}</th>
                    <th style={thStyle}>{t('studentAdmin.report.completed')}</th>
                    <th style={thStyle}>{t('studentAdmin.report.inProgress')}</th>
                    <th style={thStyle}>{t('studentAdmin.report.notStarted')}</th>
                    <th style={thStyle}>{t('studentAdmin.report.averageScore')}</th>
                    <th style={thStyle}>{t('common.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.by_class.map(row => (
                    <tr key={row.class_id}>
                      <td style={tdStyle}>
                        <Link to={`/classes/${row.class_id}`} style={{ fontWeight: 600 }}>
                          {row.class_name}
                        </Link>
                      </td>
                      <td style={tdStyle}>{row.school_year || '—'}</td>
                      <td style={tdStyle}>{row.students}</td>
                      <td style={tdStyle}>{row.assignments}</td>
                      <td style={tdStyle}>{row.completed}</td>
                      <td style={tdStyle}>{row.in_progress}</td>
                      <td style={tdStyle}>{row.not_started}</td>
                      <td style={tdStyle}>{formatAverage(row.average_score)}</td>
                      <td style={tdStyle}>
                        <span style={statusBadgeStyle(row.status === 'active')}>{t(`classes.statuses.${row.status}`)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default SchoolReportTab;
